import React, { ReactNode, useState } from 'react'

import {
  DashboardFilter,
  ParsedDashboardFilterOptions
} from 'types/GlobalDashboardFilter'

import type { TSavedDashboardFilterDto } from '@homepal/api-contract'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'
import TabSwitch from 'components_new/molecules/TabSwitch'

import Actions from '../Actions'
import CurrentFilter from '../CurrentFilter'
import SavedFilters from '../SavedFilters'

import { useTranslation } from 'language/translator'
import { TDashboard } from 'types/GlobalDashboard'

interface DashboardFilterSectionProps {
  dashboard: TDashboard
  editable: boolean
  isMobile: boolean
  isPublic: boolean
  dashboardFilter: DashboardFilter
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  savedDashboardFilter?: TSavedDashboardFilterDto | null
  savedDashboardFilters: TSavedDashboardFilterDto[]
  setEditDashboardUserFilters: (value: boolean) => void
  setDashboardFilter: (value: DashboardFilter) => void
  setSavedDashboardFilter?: (
    value: TSavedDashboardFilterDto | null,
    skipFetch?: boolean
  ) => void
}

const OuterContainer = (props: { isMobile: boolean; children: ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '0px',
      flex: '1 1 auto',
      overflowY: 'auto'
    }}
  >
    {props.children}
  </Box>
)

const DashboardFilterSection = React.memo(
  (props: DashboardFilterSectionProps) => {
    const {
      dashboard,
      dashboardFilter,
      dashboardFilterOptions,
      editable,
      isMobile,
      isPublic,
      savedDashboardFilter,
      savedDashboardFilters,
      setDashboardFilter,
      setEditDashboardUserFilters,
      setSavedDashboardFilter
    } = props

    const { t } = useTranslation()

    const [tab, setTab] = useState<'current' | 'saved'>('current')

    // if state and state function is set then allow it.
    const showSavedFilters =
      savedDashboardFilter !== undefined &&
      setSavedDashboardFilter !== undefined
    const emptyDashboardFilters = dashboardFilterOptions.size === 0
    const hideFilterSection = !editable && emptyDashboardFilters

    if (hideFilterSection) {
      return null
    }

    if (isPublic || !showSavedFilters) {
      // saved dashboard filter not allowed for api-access
      return (
        <OuterContainer isMobile={isMobile}>
          <SectionHeader
            actions={
              <Tooltip title={t('global', 'reset')}>
                <IconButton
                  onClick={() => {
                    setDashboardFilter([])
                  }}
                  size="small"
                >
                  <Icon fontSize="small" name="RestartAltOutlined" />
                </IconButton>
              </Tooltip>
            }
            divider={true}
            sx={{ mb: 2 }}
            title={t('filters', 'filters')}
          />
          <CurrentFilter
            dashboard={dashboard}
            dashboardFilter={dashboardFilter}
            dashboardFilterOptions={dashboardFilterOptions}
            setDashboardFilter={setDashboardFilter}
          />
        </OuterContainer>
      )
    }

    return (
      <OuterContainer isMobile={isMobile}>
        {editable && emptyDashboardFilters ? (
          <EmptyPlaceholder
            actions={
              <Button
                variant="text"
                onClick={() => setEditDashboardUserFilters(true)}
              >
                {t('filters', 'add_filter')}
              </Button>
            }
            background={true}
            title={t('filters', 'no_filters')}
          />
        ) : (
          <>
            <TabSwitch
              color={'primary'}
              maxHeight="40px"
              noGutter={true}
              onChange={(value) => {
                setTab(value)
              }}
              options={[
                {
                  label: t('filters', 'filters'),
                  menuOptions: editable
                    ? [
                        {
                          iconName: 'Edit',
                          onClick: () => setEditDashboardUserFilters(true),
                          title: t('global', 'edit')
                        }
                      ]
                    : undefined,
                  value: 'current'
                },
                {
                  label: t('global', 'saved'),
                  number: savedDashboardFilters.length,
                  value: 'saved'
                }
              ]}
              value={tab}
              sx={{
                bgcolor: 'background.default',
                mb: 1,
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}
            />

            {/*-- current --*/}
            <Collapse in={tab === 'current'} unmountOnExit={true}>
              <CurrentFilter
                dashboard={dashboard}
                dashboardFilter={dashboardFilter}
                dashboardFilterOptions={dashboardFilterOptions}
                setDashboardFilter={setDashboardFilter}
              />

              <Box
                sx={{
                  bottom: 0,
                  display: 'flex',
                  gap: 1,
                  position: 'sticky',
                  pt: 1,
                  zIndex: 2,
                  bgcolor: 'background.default'
                }}
              >
                <Actions
                  dashboard={dashboard}
                  dashboardFilter={dashboardFilter}
                  dashboardFilterOptions={dashboardFilterOptions}
                  savedDashboardFilters={savedDashboardFilters}
                  setDashboardFilter={setDashboardFilter}
                  setSavedDashboardFilter={setSavedDashboardFilter}
                  setTab={setTab}
                />
              </Box>
            </Collapse>

            {/*-- saved --*/}
            <Collapse in={tab === 'saved'} unmountOnExit={true}>
              <SavedFilters
                dashboard={dashboard}
                dashboardFilterOptions={dashboardFilterOptions}
                savedDashboardFilters={savedDashboardFilters}
                savedDashboardFilter={savedDashboardFilter}
                setSavedDashboardFilter={setSavedDashboardFilter}
              />
            </Collapse>
          </>
        )}
      </OuterContainer>
    )
  }
)

export default DashboardFilterSection
